import React, { useEffect } from 'react';
import { useProduct } from '../../../hooks/useProduct';
import { useItemType } from '../../../hooks/item/useItemType';
import { useProductCategories } from '../../../hooks/useProductCategories';
import { useProductUnits } from '../../../hooks/useProductUnits';
import FormModal from '../../../components/Modals/FormModal';
import ProductItemTypeSelector from '../../../components/Product/ProductForm/ProductItemTypeSelector';
import ProductBasicInfo from '../../../components/Product/ProductForm/ProductBasicInfo';
import ServiceFields from '../../../components/Product/ProductForm/ServiceFields';
import ProductConfiguration from '../../../components/Product/ProductForm/ProductConfiguration';
import ProductImageUpload from '../../../components/Product/ProductForm/ProductImageUpload';
import ProductSubmitButton from '../../../components/Product/ProductForm/ProductSubmitButton';
import SectionCollapsibleCard from '../../FormSections/SectionCollapsibleCard';
import ProductExternalIdentifiersSection from '../../Product/form-sections/ProductExternalIdentifiersSection';
import ProductTraceability from '../../Product/form-sections/ProductTraceability';
import ProductAttributesVariant from '../../Product/form-sections/ProductAttributesVariant';
import ProductConsumableSection from '../../Product/form-sections/ProductConsumableSection';
import ProductDimensionsSection from '../../Product/form-sections/ProductDimensionsSection';
import ProductSpecialConditionsSection from '../../Product/form-sections/ProductSpecialConditionsSection';
import ProductStorageLocationSection from '../../Product/form-sections/ProductStorageLocationSection';
import { useProductSubCategories } from '../../../hooks/product/useProductSubCategories';
import ProductCommercialClassificationSection from '../../Product/form-sections/ProductCommercialClassificationSection';
import { useBrands } from '../../../hooks/product/useBrands';
import ProductAdditionalCharges from '../../Product/ProductForm/ProductAdditionalCharges';
import ProductStockControlSection from '../../Product/form-sections/ProductStockControlSection';
import ProductDiscountSection from '../../Product/form-sections/ProductDiscountSection';
import useSystemFeatures from '../../../hooks/system/useSystemFeatures';
import { SYSTEM_FEATURE } from '../../../types/system/features';

interface ProductEditPopupProps {
  popupOpen: boolean;
  onClose: () => void;
}

const ProductEditPopup: React.FC<ProductEditPopupProps> = ({
  popupOpen,
  onClose,
}) => {
  const {
    form,
    updateField,
    handlerGetProductImage,
    isSubmitting,
    handlerAddOrUpdateProduct,
  } = useProduct();

  const { itemTypeDropdown, fetchItemTypeDropdown } = useItemType();

  const { isEnabled } = useSystemFeatures();
  const showRestaurantCharges = isEnabled(SYSTEM_FEATURE.RESTAURANT);

  const {
    fetchProductCategoriesDropdown,
    productCategoriesDropdown: categoryOptions,
  } = useProductCategories();

  const { brandsDropdown, fetchBrandsDropdown } = useBrands();

  const { subCategories, loadSubCategoriesByCategory } =
    useProductSubCategories();

  const {
    handlerAddProductUnit,
    fetchProductUnitsDropdown,
    productUnitsDropdown: unitOptions,
  } = useProductUnits();

  // 🔁 Cargar catálogos cuando se abre el modal
  useEffect(() => {
    if (popupOpen) {
      fetchItemTypeDropdown();
      fetchProductCategoriesDropdown();
      fetchProductUnitsDropdown();
      fetchBrandsDropdown();
    }
  }, [popupOpen]);

  // 📌 Cargar imagen solo si existe
  useEffect(() => {
    const fetchImage = async () => {
      if (form.imagePath) {
        const imageUrl = await handlerGetProductImage(form.imagePath);
        updateField('imageUrl', imageUrl);
      }
    };
    fetchImage();
  }, [form.imagePath]);

  useEffect(() => {
    if (popupOpen && form.categoryId) {
      loadSubCategoriesByCategory(form.categoryId);
    }
  }, [popupOpen, form.categoryId]);

  const isService = form.itemTypeCode === 2;

  return (
    <FormModal
      isOpen={popupOpen}
      onClose={() => {
        onClose();
      }}
      title={`Editar ${form.itemTypeCode === 2 ? 'Servicio' : 'Producto'}`}
      maxWidth="max-w-6xl"
    >
      <div className="flex flex-col gap-8">
        <ProductItemTypeSelector
          value={form.itemTypeId}
          options={itemTypeDropdown}
          onChange={({ id, code }) => {
            updateField('itemTypeId', id);
            updateField('itemTypeCode', code);
          }}
          disabled={!!form.id} // Bloqueamos tipo de ítem en edición
        />

        {!isService ? (
          <ProductBasicInfo
            form={form}
            onChange={(field, value) => updateField(field, value)}
          />
        ) : (
          <ServiceFields
            form={form}
            onChange={(field, value) => updateField(field, value)}
          />
        )}

        <ProductConfiguration
          form={form}
          unitOptions={unitOptions}
          onChange={(field, value) => updateField(field, value)}
          onReloadUnits={fetchProductUnitsDropdown}
          onUnitCreated={handlerAddProductUnit}
        />

        {showRestaurantCharges && (
          <SectionCollapsibleCard
            title="Cargos adicionales"
            message="Clasifica la participación del producto en propina y turismo."
            defaultOpen
          >
            <ProductAdditionalCharges form={form} onChange={updateField} />
          </SectionCollapsibleCard>
        )}

        <SectionCollapsibleCard
          title="Clasificación Comercial"
          message="Permite clasificar productos y servicios dentro del catálogo."
          defaultOpen
        >
          <ProductCommercialClassificationSection
            form={form}
            onChange={updateField}
            brandOptions={brandsDropdown}
            categoryOptions={categoryOptions}
            subCategoryOptions={subCategories}
            loadSubCategoriesByCategory={loadSubCategoriesByCategory}
          />
        </SectionCollapsibleCard>

        {!isService && (
          <SectionCollapsibleCard
            title="Control de Inventario"
            message="Define si este producto debe controlarse estrictamente, sin importar la configuración general de ventas con stock negativo."
          >
            <ProductStockControlSection form={form} onChange={updateField} />
          </SectionCollapsibleCard>
        )}

        <SectionCollapsibleCard
          title="Descuentos"
          message="Define si este producto puede recibir descuentos (manuales o el descuento global de la venta/cotización)."
        >
          <ProductDiscountSection form={form} onChange={updateField} />
        </SectionCollapsibleCard>

        <ProductImageUpload
          file={form.file}
          imageUrl={form.imageUrl}
          onUpload={(file) => updateField('file', file)}
          onDelete={() => {
            updateField('file', null);
            updateField('imageUrl', '');
          }}
        />

        {!isService ? (
          <>
            <SectionCollapsibleCard
              title="Identificadores Externos"
              message="Define códigos alternativos para compatibilidad con inventarios, proveedores y sistemas externos."
            >
              <ProductExternalIdentifiersSection
                form={form}
                onChange={updateField}
              />
            </SectionCollapsibleCard>

            <SectionCollapsibleCard
              title="Trazabilidad del producto"
              message="Control de lote, batch y fecha de vencimiento para seguimiento e inventario."
            >
              <ProductTraceability form={form} onChange={updateField} />
            </SectionCollapsibleCard>

            <SectionCollapsibleCard
              title="Atributos y Variantes"
              message="Característica opcional del producto"
            >
              <ProductAttributesVariant form={form} onChange={updateField} />
            </SectionCollapsibleCard>

            <SectionCollapsibleCard
              title="Consumible"
              message="Indica si el producto se consume al usarse"
            >
              <ProductConsumableSection form={form} onChange={updateField} />
            </SectionCollapsibleCard>

            <SectionCollapsibleCard
              title="Dimensiones y Logística"
              message="Información útil para almacenamiento, empaque y logística."
            >
              <ProductDimensionsSection form={form} onChange={updateField} />
            </SectionCollapsibleCard>

            <SectionCollapsibleCard
              title="Condiciones Especiales"
              message="Afectan almacenamiento, logística y manipulación."
            >
              <ProductSpecialConditionsSection
                form={form}
                onChange={updateField}
              />
            </SectionCollapsibleCard>

            <SectionCollapsibleCard
              title="Ubicación Física"
              message="Datos para localizar físicamente el producto en almacén."
            >
              <ProductStorageLocationSection
                form={form}
                onChange={updateField}
              />
            </SectionCollapsibleCard>
          </>
        ) : null}

        <ProductSubmitButton
          formId={form.id}
          isSubmitting={isSubmitting}
          itemTypeCode={form.itemTypeCode}
          onClick={() => handlerAddOrUpdateProduct(form, form.file)}
        />
      </div>
    </FormModal>
  );
};

export default ProductEditPopup;
